import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

interface ToggleOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface EnhancedToggleProps {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'success' | 'primary' | 'hana';
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const sizeClasses = {
  sm: {
    container: 'min-w-[160px] sm:min-w-[200px] p-0.5',
    button: 'px-3 py-2 text-sm min-h-[36px]',
    text: 'text-xs sm:text-sm',
  },
  md: {
    container: 'min-w-[200px] sm:min-w-[240px] p-1',
    button: 'px-4 py-3 sm:px-6 sm:py-3 text-sm sm:text-base min-h-[44px]',
    text: 'text-sm sm:text-base',
  },
  lg: {
    container: 'min-w-[240px] sm:min-w-[280px] p-1.5',
    button: 'px-6 py-4 sm:px-8 sm:py-4 text-base sm:text-lg min-h-[52px]',
    text: 'text-base sm:text-lg',
  },
};

const variantClasses = {
  default: {
    container: 'bg-gray-100',
    slider: 'bg-gray-700',
    activeText: 'text-white',
    inactiveText: 'text-gray-600 hover:text-gray-900',
    inactiveHover: 'hover:bg-white/50',
    focus: 'focus:ring-gray-500',
  },
  success: {
    container: 'bg-green-100',
    slider: 'bg-green-600',
    activeText: 'text-white',
    inactiveText: 'text-green-700 hover:text-green-900',
    inactiveHover: 'hover:bg-green-50',
    focus: 'focus:ring-green-500',
  },
  primary: {
    container: 'bg-blue-100',
    slider: 'bg-blue-600',
    activeText: 'text-white',
    inactiveText: 'text-blue-700 hover:text-blue-900',
    inactiveHover: 'hover:bg-blue-50',
    focus: 'focus:ring-blue-500',
  },
  hana: {
    container: 'bg-gray-100',
    slider: 'bg-hana-green',
    activeText: 'text-white',
    inactiveText: 'text-gray-600 hover:text-gray-900',
    inactiveHover: 'hover:bg-white/50',
    focus: 'focus:ring-hana-green',
  },
};

export const EnhancedToggle: React.FC<EnhancedToggleProps> = ({
  options,
  value,
  onChange,
  size = 'md',
  variant = 'default',
  disabled = false,
  className,
  ariaLabel,
}) => {
  const sizeConfig = sizeClasses[size];
  const variantConfig = variantClasses[variant];
  
  const selectedIndex = options.findIndex(option => option.value === value);
  const isValidSelection = selectedIndex !== -1;

  const handleKeyDown = (event: React.KeyboardEvent, optionValue: string) => {
    if (disabled) return;
    
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onChange(optionValue);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const currentIndex = options.findIndex(opt => opt.value === value);
      const nextIndex = event.key === 'ArrowLeft' 
        ? (currentIndex - 1 + options.length) % options.length
        : (currentIndex + 1) % options.length;
      onChange(options[nextIndex].value);
    }
  };

  const getSliderPosition = () => {
    if (!isValidSelection) return { left: '4px', width: '0px' };
    
    const percentage = (selectedIndex / options.length) * 100;
    const width = `calc(${100 / options.length}% - 4px)`;
    const left = `calc(${percentage}% + 2px)`;
    
    return { left, width };
  };

  return (
    <div
      className={cn(
        'relative rounded-2xl transition-opacity',
        sizeConfig.container,
        variantConfig.container,
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      role="tablist"
      aria-label={ariaLabel || 'Toggle options'}
      aria-disabled={disabled}
    >
      {/* Animated Background Slider */}
      {isValidSelection && (
        <motion.div
          className={cn(
            'absolute top-1 bottom-1 rounded-xl shadow-lg',
            variantConfig.slider
          )}
          initial={false}
          animate={getSliderPosition()}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25,
          }}
        />
      )}

      {/* Toggle Buttons */}
      <div className="relative flex">
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isOptionDisabled = disabled || option.disabled;
          
          return (
            <button
              key={option.value}
              onClick={() => !isOptionDisabled && onChange(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              disabled={isOptionDisabled}
              className={cn(
                'relative z-10 flex-1 font-medium text-center transition-all duration-200 rounded-xl touch-manipulation',
                sizeConfig.button,
                sizeConfig.text,
                variantConfig.focus,
                isSelected 
                  ? cn(variantConfig.activeText, 'shadow-sm') 
                  : cn(
                      variantConfig.inactiveText,
                      !isOptionDisabled && variantConfig.inactiveHover
                    ),
                isOptionDisabled && 'cursor-not-allowed opacity-50'
              )}
              role="tab"
              aria-selected={isSelected}
              aria-controls={`content-${option.value}`}
              aria-disabled={isOptionDisabled}
              tabIndex={isSelected ? 0 : -1}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EnhancedToggle; 